import { useEffect, type Dispatch, type SetStateAction } from 'react'
import type { AuthState } from '../../types'
import { getCurrentUser, isAuthenticated as checkAuth, refreshToken } from '../../services/auth'
import { tokenStorage, ApiError, onAuthLogout } from '../../services/api'

const UNAUTHENTICATED: AuthState = {
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: false,
}

async function tryRefresh(setState: Dispatch<SetStateAction<AuthState>>): Promise<void> {
  try {
    const response = await refreshToken()
    setState({
      user: response.user,
      accessToken: response.accessToken,
      isAuthenticated: true,
      isLoading: false,
    })
  } catch {
    tokenStorage.clearTokens()
    setState(UNAUTHENTICATED)
  }
}

async function loadCurrentUser(setState: Dispatch<SetStateAction<AuthState>>): Promise<void> {
  try {
    const user = await getCurrentUser()
    setState({
      user,
      accessToken: tokenStorage.getAccessToken(),
      isAuthenticated: true,
      isLoading: false,
    })
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized) {
      await tryRefresh(setState)
      return
    }
    tokenStorage.clearTokens()
    setState(UNAUTHENTICATED)
  }
}

export function useAuthInit(setState: Dispatch<SetStateAction<AuthState>>): void {
  useEffect(() => {
    const initAuth = async (): Promise<void> => {
      if (!checkAuth()) {
        setState((prev) => ({ ...prev, isLoading: false }))
        return
      }
      await loadCurrentUser(setState)
    }
    void initAuth()
  }, [setState])

  useEffect(() => {
    const unsubscribe = onAuthLogout(() => {
      setState(UNAUTHENTICATED)
    })
    return unsubscribe
  }, [setState])
}
